import { Component, ViewChild } from '@angular/core';
import { FieldArrayType, FormlyFieldConfig } from '@ngx-formly/core';
import { ModalComponent } from '@shared/components/modal';
import * as _ from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { LectureResourceV3 } from '../../../models/v2';

export type RepeatProps = {
  disabled?: boolean;
  isButton?: 'true' | 'false';
  deleteConfirmation?: boolean;
  max?: number;
  min?: number;
  fieldChanges: BehaviorSubject<any>;
  inputAppendClass?: string[];
  customLabel?: boolean;
  onDelete?: (field: any, modelItem: any) => void;
};

@Component({
  selector: 'formly-repeat-section',
  template: `
    <div
      *ngFor="let field of field.fieldGroup; let i = index"
      [ngClass]="props.inputAppendClass ? props.inputAppendClass.join(' ') : ''"
      class="flex"
    >
      <ng-container *ngIf="!props.customLabel">
        <formly-field class="relative w-full" [field]="field"></formly-field>
      </ng-container>
      <ng-container *ngIf="props.customLabel">
        <span class="relative flex w-full" *ngIf="{ fileInfo: fileInfo$ | async } as vm">
          <ng-container *ngIf="!(vm?.fileInfo)![i]?.label; else hasInfo">
            <formly-field class="relative w-full" [field]="field"></formly-field>
          </ng-container>
          <ng-template #hasInfo>
            <div class="inline-flex w-full items-center gap-3 p-2">
              <div class="rounded-sm bg-slate-200 p-[3px]">
                <svg class="w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 19" fill="none">
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M15.7142 16.2765C15.7142 16.894 15.4685 17.487 15.0323 17.924C14.5952 18.3603 14.0023 18.6059 13.3847 18.6059H3.22004C2.60253 18.6059 2.00959 18.3603 1.57251 17.924C1.13627 17.487 0.890625 16.894 0.890625 16.2765V2.72358C0.890625 2.10607 1.13627 1.51313 1.57251 1.07605C2.00959 0.639812 2.60253 0.394165 3.22004 0.394165H10.4929C10.8859 0.394165 11.2629 0.550024 11.5407 0.828706L15.2796 4.56762C15.5583 4.84546 15.7142 5.2224 15.7142 5.61544V16.2765ZM14.4436 16.2765V5.61544C14.4436 5.55953 14.4215 5.50532 14.3817 5.46551L10.6428 1.72659C10.603 1.68678 10.5488 1.66475 10.4929 1.66475H3.22004C2.93881 1.66475 2.6703 1.77656 2.47124 1.97478C2.27303 2.17384 2.16121 2.44235 2.16121 2.72358V16.2765C2.16121 16.5577 2.27303 16.8263 2.47124 17.0253C2.6703 17.2235 2.93881 17.3353 3.22004 17.3353H13.3847C13.666 17.3353 13.9345 17.2235 14.1335 17.0253C14.3318 16.8263 14.4436 16.5577 14.4436 16.2765Z"
                    fill="currentColor"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M10.2109 1.45292C10.2109 1.10224 10.4955 0.817627 10.8462 0.817627C11.1969 0.817627 11.4815 1.10224 11.4815 1.45292V4.41763C11.4815 4.53452 11.5764 4.62939 11.6933 4.62939H14.658C15.0087 4.62939 15.2933 4.914 15.2933 5.26469C15.2933 5.61537 15.0087 5.89998 14.658 5.89998H11.6933C10.8742 5.89998 10.2109 5.23673 10.2109 4.41763V1.45292Z"
                    fill="currentColor"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M4.91654 8.86544C4.56586 8.86544 4.28125 8.58083 4.28125 8.23014C4.28125 7.87946 4.56586 7.59485 4.91654 7.59485H11.693C12.0437 7.59485 12.3283 7.87946 12.3283 8.23014C12.3283 8.58083 12.0437 8.86544 11.693 8.86544H4.91654Z"
                    fill="currentColor"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M4.91654 11.8294C4.56586 11.8294 4.28125 11.5448 4.28125 11.1941C4.28125 10.8434 4.56586 10.5588 4.91654 10.5588H11.693C12.0437 10.5588 12.3283 10.8434 12.3283 11.1941C12.3283 11.5448 12.0437 11.8294 11.693 11.8294H4.91654Z"
                    fill="currentColor"
                  />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M4.91654 14.7934C4.56586 14.7934 4.28125 14.5088 4.28125 14.1581C4.28125 13.8074 4.56586 13.5228 4.91654 13.5228H8.72831C9.07899 13.5228 9.3636 13.8074 9.3636 14.1581C9.3636 14.5088 9.07899 14.7934 8.72831 14.7934H4.91654Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <a
                [href]="(vm?.fileInfo)![i]?.file_url"
                class="ar:text-right grow truncate text-sm font-medium"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ (vm?.fileInfo)![i]?.label ?? '' | truncate : 46 : ' ...' }}
              </a>
            </div>
          </ng-template>
        </span>
      </ng-container>
      <ng-container *ngIf="props.disabled !== true">
        <div class="col-sm-2 d-flex align-items-center" *ngIf="!isMin()">
          <formly-RepeatButton
            [field]="field"
            [id]="i"
            tempalate="delete"
            (clickEvent)="handleClick($event)"
          ></formly-RepeatButton>
          <!--  -->
        </div>
      </ng-container>
    </div>
    <ng-container *ngIf="props.disabled !== true && props.isButton !== 'false'">
      <div class="ar:text-right mt-3 max-sm:mt-0" *ngIf="!isMax()">
        <formly-RepeatButton [field]="field" (clickEvent)="handleClick($event)"></formly-RepeatButton>
      </div>
    </ng-container>

    <ng-container *ngIf="props.disabled !== true">
      <common-modal #deleModal title="Delete Confirmation" customClass="sm:w-full sm:max-w-md" color="red">
        <ng-container content>
          <formly-DeleteConfirmation (clickEvent)="handleClick($event)" [id]="toRemove"></formly-DeleteConfirmation>
          <!--  -->
        </ng-container>
      </common-modal>
    </ng-container>
  `,
})
export class RepeatTypeComponent extends FieldArrayType<FormlyFieldConfig<RepeatProps>> {
  @ViewChild('deleModal') deleModal!: ModalComponent;

  fileInfo$ = new BehaviorSubject<LectureResourceV3[]>([]);

  constructor() {
    super();
  }

  ngOnInit() {
    this.props.fieldChanges = new BehaviorSubject<any>(this);
    this.triggerFieldChange();
    this.fileInfo$.next(this.model ?? []);
  }

  toRemove: number = 0;
  removeItem(i: number) {
    if (this.props['deleteConfirmation']) {
      this.toRemove = i;
      this.deleModal.open();
    } else {
      this.delete(i);
    }
  }

  delete(i: number) {
    if (this.props['onDelete']) {
      this.props['onDelete'](this.field, this.model[i]);
    }
    this.remove(i);
  }

  isMax() {
    if (!this.props.max) {
      return false;
    }
    if (this.props?.max && this.field.fieldGroup?.length) {
      if (this.field.fieldGroup?.length >= this.props?.max) {
        return true;
      }
    }
    return false;
  }

  isMin() {
    const itemLength = _.values(this.field.fieldGroup).length;
    if (!this.props.min) {
      return false;
    }
    if (this.props?.min) {
      if (this.props?.min >= itemLength) {
        return true;
      }
    }
    return false;
  }

  triggerFieldChange() {
    this.props.fieldChanges.next(this);
  }

  handleClick(event: { action: string; id?: string | number }) {
    if (event.action == 'add') {
      this.add();
    }
    if (event.action == 'delete' && (event.id || event.id === 0)) {
      this.removeItem(Number(event.id));
    }
    if (event.action == 'delete-confirm') {
      this.delete(Number(event.id));
      this.deleModal.close();
    }
    if (event.action == 'delete-cancel') {
      this.deleModal.close();
    }
    this.triggerFieldChange();
  }
}

/**  Copyright 2021 Formly. All Rights Reserved.
    Use of this source code is governed by an MIT-style license that
    can be found in the LICENSE file at https://github.com/ngx-formly/ngx-formly/blob/main/LICENSE */
